import { z } from "zod";

import {
  ATTACHMENT_MAX_COUNT,
  DocumentAttachment,
  ImageAttachment,
  LinkAttachment,
} from "../attachments/attachments.types.ts";
import { UUID } from "../generic/uuid.ts";

import { MIN_ARTIFACT_COUNT } from "./artifact.ts";
import {
  EditVariant,
  OperationVariant,
  Operation,
  RegenerateVariant,
} from "./operation.ts";

////////////////////////////////////////////////////////
// Chat history
////////////////////////////////////////////////////////
export const ChatHistoryRequestPayload = z.object({
  fileId: UUID,
});
export type ChatHistoryRequestPayload = z.infer<
  typeof ChatHistoryRequestPayload
>;

////////////////////////////////////////////////////////
// Generation status
////////////////////////////////////////////////////////
export const GenerationStatusRequestPayload = z.object({
  fileId: UUID,
});
export type GenerationStatusRequestPayload = z.infer<
  typeof GenerationStatusRequestPayload
>;

////////////////////////////////////////////////////////
// Storage
////////////////////////////////////////////////////////
export const UploadFileChatAttachmentsRequestPayload = z.instanceof(FormData);
export type UploadFileChatAttachmentsRequestPayload = z.infer<
  typeof UploadFileChatAttachmentsRequestPayload
>;

export const DeleteFileChatAttachmentsRequestPayload = z.object({
  path: z.string().trim().min(1),
});
export type DeleteFileChatAttachmentsRequestPayload = z.infer<
  typeof DeleteFileChatAttachmentsRequestPayload
>;

////////////////////////////////////////////////////////
// Transcribe
////////////////////////////////////////////////////////
export const TranscribeRequestPayload = z.instanceof(FormData);
export type TranscribeRequestPayload = z.infer<typeof TranscribeRequestPayload>;

export const TranscribeResponsePayload = z.object({
  text: z.string(),
});
export type TranscribeResponsePayload = z.infer<
  typeof TranscribeResponsePayload
>;

////////////////////////////////////////////////////////
// AI
////////////////////////////////////////////////////////
export const DocumentAttachmentRequestData = DocumentAttachment.pick({
  id: true,
  type: true,
  path: true,
});
export type DocumentAttachmentRequestData = z.infer<
  typeof DocumentAttachmentRequestData
>;

export const ImageAttachmentRequestData = ImageAttachment.pick({
  id: true,
  type: true,
  path: true,
});
export type ImageAttachmentRequestData = z.infer<
  typeof ImageAttachmentRequestData
>;

export const LinkAttachmentRequestData = LinkAttachment.pick({
  id: true,
  type: true,
  url: true,
});
export type LinkAttachmentRequestData = z.infer<
  typeof LinkAttachmentRequestData
>;

export const AttachmentRequestData = z.discriminatedUnion("type", [
  DocumentAttachmentRequestData,
  ImageAttachmentRequestData,
  LinkAttachmentRequestData,
]);
export type AttachmentRequestData = z.infer<typeof AttachmentRequestData>;

export const BaseAiRequestPayload = z.object({
  fileId: UUID,
  message: z.string().trim().min(1), // TODO: Set max(6000) + validate on the frontend + ensure that the database does not contain longer prompts
  // Minted by the client so it knows the turn's identity before the server has
  // broadcast the request message back to it.
  requestMessageId: UUID,
});
type BaseAiRequestPayload = z.infer<typeof BaseAiRequestPayload>;

export const UndefinedAiRequestPayload = BaseAiRequestPayload.extend({
  attachments: z.array(AttachmentRequestData).min(0).max(ATTACHMENT_MAX_COUNT),
  operation: z.undefined(),
  elementIds: z.array(UUID),
  // Set when this message answers a clarification the agent asked.
  clarificationId: UUID.optional(),
});
export type UndefinedAiRequestPayload = z.infer<
  typeof UndefinedAiRequestPayload
>;

export const RegenerateAiRequestPayload = BaseAiRequestPayload.extend({
  attachments: z.array(AttachmentRequestData).length(0),
  operation: z.literal(Operation.enum.regenerate),
  screenIds: z.array(UUID).length(MIN_ARTIFACT_COUNT),
  operationVariant: RegenerateVariant,
});
export type RegenerateAiRequestPayload = z.infer<
  typeof RegenerateAiRequestPayload
>;

export const ExpandAiRequestPayload = BaseAiRequestPayload.extend({
  attachments: z.array(AttachmentRequestData).length(0),
  operation: z.literal(Operation.enum.expand),
  operationVariant: OperationVariant,
  screenIds: z.array(UUID).length(MIN_ARTIFACT_COUNT),
});
export type ExpandAiRequestPayload = z.infer<typeof ExpandAiRequestPayload>;

export const EditAiRequestPayload = BaseAiRequestPayload.extend({
  attachments: z.array(AttachmentRequestData).min(0).max(ATTACHMENT_MAX_COUNT),
  operation: z.literal(Operation.enum.edit),
  elementIds: z.array(UUID).min(MIN_ARTIFACT_COUNT),
  operationVariant: EditVariant,
});
export type EditAiRequestPayload = z.infer<typeof EditAiRequestPayload>;

export const AiRequestPayload = z.discriminatedUnion("operation", [
  UndefinedAiRequestPayload,
  RegenerateAiRequestPayload,
  ExpandAiRequestPayload,
  EditAiRequestPayload,
]);
export type AiRequestPayload = z.infer<typeof AiRequestPayload>;

export const CancelAiRequestPayload = z.object({
  fileId: UUID,
});
export type CancelAiRequestPayload = z.infer<typeof CancelAiRequestPayload>;
